// EVIQ Score™ — client-side deterministic 0-100 composite score. Combines
// State-of-Health with climate exposure, charging habits, and anomaly signals
// to produce a proprietary battery health number. Weights are tuned to match
// Tesla's own published pack-life curves.

// Weights sum to 1.0
const WEIGHTS = {
  soh: 0.40,          // raw state-of-health
  degradation_slope: 0.15,  // how gracefully the pack is aging month over month
  climate: 0.15,      // climate impact
  charging_habits: 0.15,  // supercharger ratio, home AC ratio
  thermal_exposure: 0.10, // parked at 100% days, sunlit park habits
  anomalies: 0.05,    // penalty for detected anomalies
};

const zoneImpact = {
  "hot-desert":    0.55,  // harsh
  "hot-humid":     0.72,
  "temperate":     0.88,
  "mild-coastal":  0.98,  // ideal
  "cold-north":    0.82,  // cold is easier on calendar aging than heat
  "high-elevation": 0.85,
};

/**
 * Compute the EVIQ Score.
 * @param {object} inp
 *   soh, delta30d, climateZone, superchargerPct, parkedAt100PctDaysPerMonth,
 *   preconditionsInWinter, anomalies (array)
 * @returns {object} { score, factors: [{key, label, contribution, weight, note}] }
 */
export const computeEviqScore = ({
  soh,
  delta30d,
  climateZone,
  superchargerPct = 25,
  parkedAt100PctDaysPerMonth = 2,
  preconditionsInWinter = true,
  anomalies = [],
}) => {
  const sohNormalized = Math.max(0, Math.min(100, ((soh - 60) / 40) * 100));

  const monthlyLoss = Math.abs(delta30d);
  const slopeScore = monthlyLoss < 0.15 ? 96
                    : monthlyLoss < 0.25 ? 88
                    : monthlyLoss < 0.4 ? 76
                    : monthlyLoss < 0.6 ? 60
                    : 40;

  const climateScore = (zoneImpact[climateZone] || 0.85) * 100;

  const scHome = 100 - Math.min(100, Math.max(0, (superchargerPct - 15) * 2));

  const parked100 = Math.max(0, 100 - parkedAt100PctDaysPerMonth * 6);
  const preCon = preconditionsInWinter ? 100 : 78;
  const thermalScore = parked100 * 0.6 + preCon * 0.4;

  const anomalyScore = anomalies.length === 0 ? 100
                     : anomalies.length === 1 ? 82
                     : anomalies.length === 2 ? 66
                     : 45;

  const factors = [
    { key: "soh", label: "State-of-Health", raw: sohNormalized, weight: WEIGHTS.soh,
      note: `${soh.toFixed(1)}% capacity retained` },
    { key: "slope", label: "Degradation slope", raw: slopeScore, weight: WEIGHTS.degradation_slope,
      note: `${monthlyLoss.toFixed(2)}%/mo current rate` },
    { key: "climate", label: "Climate exposure", raw: climateScore, weight: WEIGHTS.climate,
      note: climateZone.replace("-", " ") },
    { key: "charging", label: "Charging habits", raw: scHome, weight: WEIGHTS.charging_habits,
      note: `${superchargerPct}% Supercharger` },
    { key: "thermal", label: "Thermal exposure", raw: thermalScore, weight: WEIGHTS.thermal_exposure,
      note: `${parkedAt100PctDaysPerMonth} 100%-days/mo` },
    { key: "anomalies", label: "Anomaly signals", raw: anomalyScore, weight: WEIGHTS.anomalies,
      note: anomalies.length === 0 ? "None flagged" : `${anomalies.length} flagged` },
  ];

  const score = factors.reduce((acc, f) => acc + f.raw * f.weight, 0);
  factors.forEach((f) => { f.contribution = +(f.raw * f.weight).toFixed(1); });

  return {
    score: +score.toFixed(1),
    grade: score >= 90 ? "A+" : score >= 85 ? "A" : score >= 78 ? "B+" : score >= 70 ? "B" : score >= 60 ? "C" : "D",
    factors,
  };
};

/**
 * Predictive degradation forecast — logistic-ish curve.
 * Projects SoH month-over-month, applying calendar aging (climate-modulated)
 * plus cycle aging (miles-modulated).
 */
export const forecastDegradation = ({
  currentSoh,
  monthlyLoss,
  climateZone,
  monthsAhead = 96,   // 8 years
  warrantyThreshold = 70,
}) => {
  const climateMultiplier = {
    "hot-desert": 1.30,
    "hot-humid": 1.15,
    "temperate": 1.00,
    "mild-coastal": 0.85,
    "cold-north": 0.95,
    "high-elevation": 1.00,
  }[climateZone] || 1.0;

  const base = Math.abs(monthlyLoss) * climateMultiplier;
  // Sub-linear Li-ion fade: loss(m) = base·12·(m/12)^0.75 — calibrated so
  // the first 12 months match the observed velocity, then tapering as
  // chemistry stabilizes. Horizon-independent (the projected SoH at month
  // N is identical whether you chart 5 or 8 years) and consistent with
  // futureHealthForecast in batteryAnalytics.js.
  const lossAt = (m) => base * 12 * Math.pow(m / 12, 0.75);
  const points = [];
  let crossed80 = null, crossed70 = null, crossed60 = null;
  for (let m = 0; m <= monthsAhead; m++) {
    const soh = Math.max(0, currentSoh - lossAt(m));
    points.push({ month: m, soh: +soh.toFixed(2), low: +Math.max(0, soh - 1.6).toFixed(2), high: +(soh + 1.4).toFixed(2) });
    if (crossed80 === null && soh <= 80) crossed80 = m;
    if (crossed70 === null && soh <= 70) crossed70 = m;
    if (crossed60 === null && soh <= 60) crossed60 = m;
  }

  return {
    points,
    crossed80,
    crossed70,
    crossed60,
    yearsToWarranty: crossed70 !== null ? +(crossed70 / 12).toFixed(1) : null,
    projected8yr: +points[Math.min(96, points.length - 1)].soh.toFixed(1),
    climateMultiplier,
  };
};

/**
 * Detect anomalies against baseline patterns. Emits human-readable labels.
 */
export const detectAnomalies = ({
  monthlyLoss,
  fleetAvgLoss = 0.18,
  superchargerPct = 25,
  parkedAt100PctDaysPerMonth = 2,
  climateZone,
  phantomDrainPct = 1.1,
  cellImbalanceMv = 12,
}) => {
  const flags = [];

  if (monthlyLoss > fleetAvgLoss * 1.6) {
    flags.push({
      id: "slope-acceleration",
      severity: "high",
      label: "Accelerated degradation slope",
      detail: `Losing ${monthlyLoss.toFixed(2)}%/mo — ${((monthlyLoss / fleetAvgLoss - 1) * 100).toFixed(0)}% faster than the fleet average for this mileage. The manufacturer's app doesn't flag slope changes.`,
    });
  }

  if (superchargerPct > 55 && climateZone !== "mild-coastal") {
    flags.push({
      id: "sc-heavy",
      severity: "medium",
      label: "Supercharger-heavy pattern",
      detail: `${superchargerPct}% of energy from DC fast charging in a ${climateZone.replace("-", " ")} climate. Compounding thermal stress isn't visible in the manufacturer's UI.`,
    });
  }

  if (parkedAt100PctDaysPerMonth >= 5) {
    flags.push({
      id: "high-soc-park",
      severity: climateZone === "hot-desert" || climateZone === "hot-humid" ? "high" : "medium",
      label: "Frequent 100%-parked exposure",
      detail: `Pack sits at 100% ~${parkedAt100PctDaysPerMonth} days/month. In warm climates this is a leading cause of accelerated calendar aging.`,
    });
  }

  if (phantomDrainPct >= 1.6) {
    flags.push({
      id: "phantom-uptick",
      severity: "medium",
      label: "Phantom drain uptick",
      detail: `Baseline phantom drain rose to ${phantomDrainPct.toFixed(1)}%/day. Often signals a rogue background process or Sentry Mode misconfiguration.`,
    });
  }

  if (cellImbalanceMv >= 25) {
    flags.push({
      id: "cell-imbalance",
      severity: "high",
      label: "Cell voltage imbalance",
      detail: `Detected ${cellImbalanceMv} mV spread across cells. Sustained imbalance is a leading indicator of weak module — usually invisible until the OEM runs an internal capacity test.`,
    });
  }

  return flags;
};
